import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";

import {
  isDemoWhitelabelSubdomain,
  offersAllowedAgentIds,
  HIDE_POWERED_BY_AMBER_PARTNER_IDS,
} from "constants/index";
import filterRecentlyVisitedForWhitelabel, {
  mergeLocalizedWhitelabelConfig,
} from "@Utils/whitelabelHelper";

const useWhitelabel = () => {
  const userLanguage = useSelector(({ appData }: AppState) => appData.userLanguage);
  const baseConfig = useSelector(({ whitelabelData }: AppState) => whitelabelData.config);
  const configTranslations = useSelector(
    ({ whitelabelData }: AppState) => whitelabelData.config_translations,
  );

  const localizedConfig = useMemo(() => {
    if (baseConfig && userLanguage && userLanguage !== "en" && configTranslations?.[userLanguage]) {
      // Deep merge, not a spread: the overlay is partial, so replacing whole
      // objects drops sibling keys the translation never carried — notably
      // footer.is_link_enabled, which gates the whole footer-link block.
      return mergeLocalizedWhitelabelConfig(baseConfig, configTranslations[userLanguage]);
    }
    return baseConfig;
  }, [baseConfig, userLanguage, configTranslations]);

  const whitelabelState = useSelector(
    ({ whitelabelData }: AppState) => ({
      isWhitelabel: whitelabelData.isWhitelabel,
      whitelabel_region_canonical_name: whitelabelData.whitelabel_region_canonical_name,
      subdomain: whitelabelData.subdomain,
      partner_id: whitelabelData.partner_id,
      partner_uuid: whitelabelData.partner_uuid,
      partnerName: whitelabelData.partner_name,
      whitelabelColors: whitelabelData.colors,
      universityMock: whitelabelData.university_mock,
      isDemoSubdomain:
        whitelabelData.isWhitelabel && isDemoWhitelabelSubdomain(whitelabelData.subdomain),
      regionCanonicalNames: whitelabelData.region_canonical_names,
      requestDetails: whitelabelData.requestDetails,
      isUniversityPartner: whitelabelData.isUniversityPartner,
      isWhitelabelDataFetched: whitelabelData.isWhitelabelDataFetched,
      citiesList: whitelabelData?.cities_list,
      universitiesList: whitelabelData?.universities_list,
      countryWiseCityData: whitelabelData?.countryWiseCityData,
      countryWiseUniversityData: whitelabelData?.countryWiseUniversityData,
      whitelabelTheme: whitelabelData?.themes,
      partnerType: whitelabelData?.partnerType,
      isPartnerCustomFormEnabled: whitelabelData?.isPartnerCustomFormEnabled,
      isCustomFormForAllInventories: whitelabelData?.isCustomFormForAllInventories,
      partnerCustomFormFieldConfig: whitelabelData?.partnerCustomFormFieldConfig,
      isUSAPartner: whitelabelData?.isUSAPartner,
      isWhitelabelSeoEnabled: whitelabelData?.isWhitelabelSeoEnabled,
      whitelabelGoogleSiteVerificationId: whitelabelData?.whitelabelGoogleSiteVerificationId,
      isPoweredByAmberEnabledForWhitelabelFooter:
        whitelabelData?.isPoweredByAmberEnabledForWhitelabelFooter,
      agentId: whitelabelData?.agentId,
      isSubPartnerRedirectToWhitelabel: whitelabelData?.isSubPartnerRedirectToWhitelabel,
    }),
    shallowEqual,
  );

  const getFilteredRecentlyVisited = () => {
    const stored = JSON.parse(localStorage.getItem("recentlyVisited") ?? "[]") || [];
    return stored.length > 0
      ? filterRecentlyVisitedForWhitelabel(
          stored,
          whitelabelState.isWhitelabel,
          whitelabelState.partner_id,
        )
      : [];
  };

  const isAllowedEmail = offersAllowedAgentIds.includes(whitelabelState.agentId);

  const shouldHidePoweredByAmber = HIDE_POWERED_BY_AMBER_PARTNER_IDS.includes(
    Number(whitelabelState.partner_id),
  );

  return {
    ...whitelabelState,
    whitelabelConfig: localizedConfig,
    isGlobalSearchEnabled: localizedConfig?.is_global_search_enabled,
    isReferralCardEnabled: localizedConfig?.is_referral_card_enabled,
    isLocationSearchRestricted:
      whitelabelState?.isWhitelabel &&
      (whitelabelState?.isUniversityPartner || !localizedConfig?.is_global_search_enabled),
    isRestrictedSearchEnabled:
      whitelabelState.isWhitelabel &&
      !whitelabelState.isUniversityPartner &&
      !localizedConfig?.is_global_search_enabled,
    isAnnouncementBannersEnabled: localizedConfig?.is_announcement_banner_enabled,
    announcementBanners: localizedConfig?.announcement_banners,
    isAllowedEmail,
    shouldHidePoweredByAmber,
    getFilteredRecentlyVisited,
  };
};

export default useWhitelabel;
